// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation). Pauses on hover, keyboard focus, hidden tab,
// and via a visible pause/play button (explicit pause is sticky until the visitor presses play).
// Cloned copies are hidden from screen readers and the tab order so content is announced once.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #hovered = false;
  #focused = false;
  #userPaused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("mouseenter", () => { this.#hovered = true; this.#sync(); }, { signal });
    this.addEventListener("mouseleave", () => { this.#hovered = false; this.#sync(); }, { signal });
    this.addEventListener("focusin", () => { this.#focused = true; this.#sync(); }, { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) { this.#focused = false; this.#sync(); }
    }, { signal });
    document.addEventListener("visibilitychange", () => this.#sync(), { signal });

    this.#toggle?.addEventListener("click", () => {
      this.#userPaused = !this.#userPaused;
      this.#updateToggle();
      this.#sync();
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  get #toggle() {
    return this.querySelector(":scope > [data-marquee-toggle]");
  }

  #sync() {
    if (!this.#animation) return;
    const shouldPause = this.#userPaused || this.#hovered || this.#focused || document.hidden;
    if (shouldPause) this.#animation.pause();
    else this.#animation.play();
  }

  #updateToggle() {
    const toggle = this.#toggle;
    if (!toggle) return;
    const label = this.#userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute("aria-label", label);
    toggle.querySelector("[data-icon-pause]")?.classList.toggle("hidden", this.#userPaused);
    toggle.querySelector("[data-icon-play]")?.classList.toggle("hidden", !this.#userPaused);
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;
    const toggle = this.#toggle;
    if (toggle) toggle.hidden = true;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    if (toggle) toggle.hidden = false;
    this.#updateToggle();
    this.#sync();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
